import { defineComponent as _defineComponent } from 'vue'
import { ref, watch, watchEffect, computed, onBeforeMount, onBeforeUnmount } from 'vue';
import { nanoid } from 'nanoid';
import type { ContentSection, Link, Text, CollectionsByIdsSelectorConfig } from '@/types/viewV2';
import TextEditor from '@/components/view/v2/TextEditor.vue';
import type { NameId } from '@/resources/views/view-types';
import { getList } from '@/data-provider/contentCollections';
import type { ContentCollection } from '@ilteducation/content-collections-types';
import eventBus from '@/foundations/services/eventBus';

// Props and emit (Vue 2 v-model: value/input)
type Props = {
  value: ContentSection[];
  realm: string;
  subscription: string;
};

type NodeKind = 'Root' | 'Section' | 'ContentItem' | 'ChildSections';

// Meta for selected node lookup
type NodeMeta = {
  id: string;
  kind: NodeKind;
  // Path of section indices from root to the section that owns the node
  sectionPath: number[]; // e.g. [0] for root sections[0], [2,1] for link target.sections[1] under root sections[2]
  // For content items, contentIndex points into the owning section's content array
  contentIndex?: number;
  // For content items, subtype is 'Link'|'Text'|'CollectionsByIds'
  subtype?: string;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'ViewsConfiguratorV2',
  props: {
    value: null,
    realm: null,
    subscription: null
  },
  emits: ["input"],
  setup(__props: any, { emit }: { emit: ({ (e: 'input', value: ContentSection[]): void }), expose: any, slots: any, attrs: any }) {

const props = __props as {
  value: ContentSection[];
  realm: string;
  subscription: string;
}




const sections = ref<ContentSection[]>(props.value || []);
watch(
  () => props.value,
  (val) => {
    if (val !== sections.value) sections.value = val || [];
  },
);
const pushUpdate = () => {
  updateCounter.value++;
  emit('input', sections.value);
};

// Drop down props
const isLoading = ref(false);
const selectedColl = ref<NameId>();
const items = ref<NameId[]>([]);

const fetchItems = async () => {
  let itemCursor: string | null = null;
  try {
    isLoading.value = true;
    do {
      const context = props.subscription.replace('-nb', '').replace('-nn', '');
      const filter = { realm: props.realm, context };
      const { data, cursor: curs } = await getList(filter, itemCursor, 50);
      if (data?.length > 0) {
        const colls: ContentCollection[] = data;
        const cRefs = colls.filter((c) => c.contents.type === 'ContentsByReferences' && c.realm === props.realm);
        items.value = items.value.concat(
          cRefs.map((c) => ({ id: c.id, name: c.name, displayName: `${c.name}${c.languages?.mainLanguage ? ` - (${c.languages.mainLanguage})` : ''}` })),
        );
      }
      itemCursor = curs as string ?? null;
    } while (itemCursor && itemCursor.length > 0);
  } catch (error) {
    itemCursor = null;
    console.error(error);
    eventBus.$emit('snackbarV2.open', { text: `Failed to load collections!`, color: 'error' });
  } finally {
    isLoading.value = false;
  }
};

onBeforeMount(async () => {
  await fetchItems();
});

const addCollections = () => {
  if (selectedColl.value) {
    contentCollectionIds.value.push(selectedColl.value.id);
  }
  selectedColl.value = undefined;
}

// ---------------------- Tree mapping ----------------------
// Node kinds
const open = ref<string[]>([]);
const active = ref<string[]>([]);
// Track last node we explicitly selected via label click/keyboard to stabilize selection
const lastClickedId = ref<string>('');
const selectNode = (id: string) => {
  lastClickedId.value = id;
  active.value = [id];
};
const onActiveUpdate = (val: string[]) => {
  // Accept tree's update, but if it cleared or changed selection right after our explicit click,
  // restore the last explicitly selected id to ensure sections with children can be selected.
  active.value = Array.isArray(val) ? val : [];
  if (lastClickedId.value && (!active.value.length || active.value[0] !== lastClickedId.value)) {
    active.value = [lastClickedId.value];
  }
};

// Ensure a node and its ancestor chain are expanded in the tree
const ensureOpen = (id: string) => {
  if (!id) return;
  const parts = id.split('-');
  // accumulate prefixes that correspond to valid node ids we generate
  const prefixes: string[] = [];
  for (let i = 0; i < parts.length; i++) {
    const prefix = parts.slice(0, i + 1).join('-');
    // Only push prefixes that exist in metaMap
    if (metaMap.value[prefix]) prefixes.push(prefix);
  }
  prefixes.forEach((p) => {
    if (!open.value.includes(p)) open.value.push(p);
  });
};

// Build tree items and meta map recursively
const metaMap = ref<Record<string, NodeMeta>>({});

const deriveTitle = (c: Link | Text | CollectionsByIdsSelectorConfig) => {
  const t: any = c as any;
  if (t.type === 'Link' || t.type === 'Text') return t.name || t.type;
  if (t.type === 'CollectionsByIds') return `Collections (${(t.ids || []).length})`;
  return 'Item';
};

const getSectionByPath = (path: number[]): ContentSection | null => {
  let list = sections.value;
  let sec: ContentSection | null = null;
  for (let i = 0; i < path.length; i++) {
    const idx = path[i];
    if (!list || idx < 0 || idx >= list.length) return null;
    sec = list[idx];
    if (i < path.length - 1) {
      // path continues: must be via a Link's child sections
      // We find the next hop by reading the next path segment from a metaMap context when building tree
      // Here we rely on callers to pass valid paths built by our tree
      // But for generic navigation, we search for the link that owns the next section list
      // Instead, we pass section lists along in callers; this function only handles root path.
    }
  }
  return sec;
};

// We need a function that, given a sectionPath, returns the array of sections at that depth and the parent link (if any)
const getSectionsArrayAtPath = (sectionPath: number[]): { list: ContentSection[]; parentLink: Link | null } => {
  let list = sections.value;
  let parentLink: Link | null = null;
  if (sectionPath.length === 0) return { list, parentLink };
  for (let depth = 0; depth < sectionPath.length; depth++) {
    const idx = sectionPath[depth];
    const sec = list[idx];
    if (!sec) return { list: [], parentLink: null } as any;
    if (depth === sectionPath.length - 1) {
      return { list, parentLink };
    }
    // Next hop must come from a Link within this section
    // We find the Link that leads to the next section index by using a synthetic mapping built during tree build
    // To avoid complex lookups here, we instead maintain a parallel pathStack during tree creation and selection.
  }
  return { list, parentLink };
};

const updateCounter = ref(0);

// Build the tree and meta using a DFS with a path context for sections
const treeItems = computed(() => {
  // Create reactive dependency on updateCounter to trigger re-render on nested changes
  const _ = updateCounter.value;

  const items: any[] = [];
  const meta: Record<string, NodeMeta> = {};

  // Recursive function to build nested sections under a child sections node
  const buildNestedSections = (
    sectionList: ContentSection[],
    parentPrefix: string,
    basePath: number[]
  ): any[] => {
    const nodes: any[] = [];

    sectionList.forEach((sec, sIdx) => {
      const secId = `${parentPrefix}-s-${sIdx}`;
      const secPath = [...basePath, sIdx];

      meta[secId] = {
        id: secId,
        kind: 'Section',
        sectionPath: secPath,
        subtype: 'NestedSection',
      } as any;

      const secNode: any = { id: secId, name: `Section: ${sec.id}`, children: [] };

      // Process content items
      (sec.content || []).forEach((c: any, ci: number) => {
        const cId = `${secId}-c-${ci}`;
        meta[cId] = {
          id: cId,
          kind: 'ContentItem',
          sectionPath: secPath,
          contentIndex: ci,
          subtype: c.type,
        } as any;

        const cNode: any = { id: cId, name: deriveTitle(c), children: [] };

        // Handle Links recursively
        if (c.type === 'Link') {
          const childSectionsId = `${cId}-sections`;
          meta[childSectionsId] = {
            id: childSectionsId,
            kind: 'ChildSections',
            sectionPath: secPath,
            contentIndex: ci,
            subtype: 'ChildSections',
          } as any;

          const childNode: any = { id: childSectionsId, name: 'Child Page', children: [] };

          // Recursively build child sections
          const childSections = (c.target && c.target.sections) || [];
          const childNodes = buildNestedSections(childSections, childSectionsId, secPath);
          childNode.children.push(...childNodes);

          cNode.children.push(childNode);
        }

        secNode.children.push(cNode);
      });

      nodes.push(secNode);
    });

    return nodes;
  };

  // Build root level sections
  const buildSectionLevel = (sectionList: ContentSection[], path: number[]) => {
    sectionList.forEach((sec, sIdx) => {
      const secPath = [...path, sIdx];
      const secId = `s-${secPath.join('-')}`;
      meta[secId] = { id: secId, kind: 'Section', sectionPath: secPath };

      const secNode: any = { id: secId, name: `Section: ${sec.id}`, children: [] };

      // Content items
      (sec.content || []).forEach((c: any, ci: number) => {
        const cId = `${secId}-c-${ci}`;
        meta[cId] = {
          id: cId,
          kind: 'ContentItem',
          sectionPath: secPath,
          contentIndex: ci,
          subtype: c.type,
        };
        const cNode: any = { id: cId, name: deriveTitle(c), children: [] };

        if (c.type === 'Link') {
          const childSectionsId = `${cId}-sections`;
          meta[childSectionsId] = {
            id: childSectionsId,
            kind: 'ChildSections',
            sectionPath: secPath,
            contentIndex: ci,
            subtype: 'ChildSections',
          };
          const childNode: any = { id: childSectionsId, name: 'Child Page', children: [] };

          // Recursively build all nested sections
          const childSections = (c.target && c.target.sections) || [];
          const nestedNodes = buildNestedSections(childSections, childSectionsId, secPath);
          childNode.children.push(...nestedNodes);

          cNode.children.push(childNode);
        }
        secNode.children.push(cNode);
      });

      items.push(secNode);
    });
  };

  buildSectionLevel(sections.value || [], []);
  metaMap.value = meta;
  return items;
});

const getMetaIcon = (id: string, open: boolean) => {
  if (metaMap.value[id].kind === 'ContentItem') {
    if (metaMap.value[id].subtype === 'CollectionsByIds') return 'mdi-developer-board';
    if (metaMap.value[id].subtype === 'Link') return 'mdi-link';
    return 'mdi-file-document-outline';
  } else if (metaMap.value[id].kind === 'ChildSections') {
    return 'mdi-web';
  }

  return open ? 'mdi-folder-open' : 'mdi-folder';
}

// ---------------------- Selection helpers ----------------------
const selectedId = computed(() => active.value[0] || '');
const selectedMeta = computed<NodeMeta | null>(() => (selectedId.value ? metaMap.value[selectedId.value] : null));

// Helper to highlight only when the active node is a Section
const isActiveSection = (id: string) => {
  return active.value[0] === id && !!metaMap.value[id] && metaMap.value[id].kind === 'Section';
};

// Utility to access owning section list and specific section by meta
const resolveSectionContext = (
  meta: NodeMeta | null,
): {
  // The array of sections at this level and index of the current section in it (if meta.kind === 'Section')
  list: ContentSection[] | null;
  index: number;
  section: ContentSection | null;
  // For nested contexts, also return the link and its sections if applicable
  link: Link | null;
  linkSections: ContentSection[] | null;
} => {
  if (!meta) return { list: null, index: -1, section: null, link: null, linkSections: null };

  // We parse the id to navigate into nested links when necessary, relying on ids we produced
  const id = meta.id;
  // Patterns
  // root section: s-0
  // content: s-0-c-2
  // child sections marker: s-0-c-2-sections
  // nested section: s-0-c-2-sections-s-1

  const parts = id.split('-');
  const getRootSection = (rootIdx: number) => sections.value[rootIdx];

  if (id.startsWith('s-') && id.indexOf('-c-') === -1 && id.indexOf('-sections') === -1) {
    // Root section
    const idx = parseInt(parts[1], 10);
    const sec = getRootSection(idx);
    return { list: sections.value, index: idx, section: sec || null, link: null, linkSections: null };
  }

  // Helper to get context for a content item or child sections or nested sections
  const matchRoot = id.match(/^s-(\d+)(.*)$/);
  if (!matchRoot) return { list: sections.value, index: -1, section: null, link: null, linkSections: null };
  const rootIdx = parseInt(matchRoot[1], 10);
  let section = sections.value[rootIdx];
  if (!section) return { list: sections.value, index: -1, section: null, link: null, linkSections: null };

  // Traverse deeper through patterns
  let rest = id.replace(/^s-\d+/, '');
  // Now rest like: "-c-2-sections-s-1-c-0-..."
  // We walk tokens
  let currentLink: Link | null = null;
  let currentSectionsList: ContentSection[] | null = sections.value;
  let currentSectionIndex = rootIdx;
  let currentSection: ContentSection | null = section;

  while (rest.length) {
    if (rest.startsWith('-c-')) {
      const m = rest.match(/^-c-(\d+)(.*)$/);
      if (!m) break;
      const ci = parseInt(m[1], 10);
      rest = m[2] || '';
      if (!currentSection) break;
      const content = currentSection.content || [];
      const item: any = content[ci];
      if (item && item.type === 'Link') {
        currentLink = item as Link;
      } else {
        currentLink = null;
      }
    } else if (rest.startsWith('-sections')) {
      rest = rest.replace(/^-sections/, '');
      // Enter into link's sections
      if (currentLink) {
        currentSectionsList = currentLink.target?.sections || [];
        // Reset currentSection so that next '-s-#' selects from link sections
        currentSection = null as any;
      }
    } else if (rest.startsWith('-s-')) {
      const m = rest.match(/^-s-(\d+)(.*)$/);
      if (!m) break;
      const sidx = parseInt(m[1], 10);
      rest = m[2] || '';
      if (!currentSectionsList) break;
      currentSectionIndex = sidx;
      currentSection = currentSectionsList[sidx] || null;
    } else {
      // Unknown token
      break;
    }
  }

  return {
    list: currentSectionsList,
    index: currentSectionIndex,
    section: currentSection,
    link: currentLink,
    linkSections: currentLink ? currentLink.target?.sections || [] : null,
  };
};

// Section content type enforcement
const sectionContentType = (sec: ContentSection): 'None' | 'Link' | 'Text' | 'CollectionsByIds' => {
  const c = (sec.content || [])[0];
  if (!c) return 'None';
  return (c as any).type;
};

const canAddTypeToSection = (sec: ContentSection, t: 'Link' | 'Text' | 'CollectionsByIds') => {
  const ct = sectionContentType(sec);
  return ct === 'None' || ct === t;
};

// ---------------------- Mutations ----------------------
const addRootSection = () => {
  sections.value.unshift({ type: 'Content', id: nanoid(5), content: [] });
  updateCounter.value++;
  pushUpdate();
};

const removeSectionAt = (list: ContentSection[], index: number) => {
  if (!list || index < 0 || index >= list.length) return;
  list.splice(index, 1);
  updateCounter.value++;
  pushUpdate();
};

// Content adders
const addContentToSection = (sec: ContentSection, type: 'Link' | 'Text' | 'CollectionsByIds'): number => {
  if (!canAddTypeToSection(sec, type)) return -1;
  const current = sec.content || [];
  let newItem: any = null;
  if (type === 'Link') {
    const child = { name: `View ${nanoid(4)}`, sections: [] } as any;
    newItem = { type: 'Link', name: 'New link', target: child, displayConfiguration: { color: '#EEEEEE', title: '', type: 'Box'} } as Link;
  } else if (type === 'Text') {
    newItem = { type: 'Text', name: 'Text block', text: '' } as Text;
  } else if (type === 'CollectionsByIds') {
    newItem = { type: 'CollectionsByIds', ids: [] } as CollectionsByIdsSelectorConfig;
  }
  const next = [...current, newItem];
  sec.content = next;
  pushUpdate();
  return next.length - 1;
};

// Reorder within content list
const moveContent = (sec: ContentSection, from: number, to: number) => {
  const list = sec.content || [];
  if (from < 0 || to < 0 || from >= list.length || to >= list.length) return;
  const next = list.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved as any);
  // Reassign array to trigger Vue 2 reactivity
  sec.content = next;
  pushUpdate();
};

const removeContentAt = (sec: ContentSection, idx: number) => {
  if (!sec.content) return;
  sec.content.splice(idx, 1);
  pushUpdate();
};

// Setters for selected content item (by selectedMeta)
const setSelectedLinkName = (v: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Link') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.type === 'Link') {
    item.name = v;
    pushUpdate();
  }
};

const setSelectedLinkDisplayType = (display: 'Box' | 'Image') => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Link') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (!item || item.type !== 'Link') return;
  if (display === 'Box') {
    item.displayConfiguration = { type: 'Box', title: item.name || 'Title', description: '', color: '#eeeeee' };
  } else if (display === 'Image') {
    item.displayConfiguration = { type: 'Image', imageId: '', title: item.name || 'Title', description: '' };
  }
  pushUpdate();
};

const setSelectedBoxField = (field: 'title' | 'description' | 'color', v: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Link') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.displayConfiguration && item.displayConfiguration.type === 'Box') {
    item.displayConfiguration[field] = v;
    pushUpdate();
  }
};

const setSelectedImageField = (field: 'imageId' | 'title' | 'description', v: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Link') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.displayConfiguration && item.displayConfiguration.type === 'Image') {
    item.displayConfiguration[field] = v;
    pushUpdate();
  }
};

const setSelectedChildName = (v: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Link') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.type === 'Link') {
    if (!item.target) item.target = { name: v || 'View', sections: [] };
    item.target.name = v;
    pushUpdate();
  }
};

const setSelectedChildSections = (newSections: ContentSection[]) => {
  const m = selectedMeta.value;
  // Allow setting when selected node is ChildSections as well
  const isChildSections = m && m.kind === 'ChildSections';
  const isLinkContent = m && m.kind === 'ContentItem' && m.subtype === 'Link';
  if (!isChildSections && !isLinkContent) return;
  const ctx = resolveSectionContext(m as NodeMeta);
  const sec = ctx.section;
  if (!sec || (m as any).contentIndex == null) return;
  const item: any = (sec.content || [])[(m as any).contentIndex];
  if (item && item.type === 'Link') {
    if (!item.target) item.target = { name: 'View', sections: [] };
    item.target.sections = newSections;
    pushUpdate();
  }
};

const setSelectedTextName = (v: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Text') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.type === 'Text') {
    item.name = v;
    pushUpdate();
  }
};

const setSelectedTextBody = (v: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'Text') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.type === 'Text') {
    item.text = v;
    pushUpdate();
  }
};

// Controls for selected node: move/remove/add
const canRemoveSelected = computed(() => {
  const m = selectedMeta.value;
  if (!m) return false;
  if (m.kind === 'Section') return true; // section can be removed (root or nested)
  if (m.kind === 'ContentItem') return true;
  return false;
});

const removeSelected = () => {
  const m = selectedMeta.value;
  if (!m) return;
  if (m.kind === 'Section') {
    // Determine parent sections array
    if (m.id.indexOf('-sections-') !== -1 || m.id.match(/-c-\d+-sections-s-/)) {
      // nested: find context link and remove from its target.sections
      const ctx = resolveSectionContext({ ...m } as any);
      const link = ctx.link;
      if (link) {
        const list = link.target?.sections || [];
        if (ctx.index >= 0 && ctx.index < list.length) list.splice(ctx.index, 1);
        pushUpdate();
      }
    } else {
      // root section
      const ctx = resolveSectionContext(m);
      if (ctx.list) removeSectionAt(ctx.list, ctx.index);
    }
  } else if (m.kind === 'ContentItem') {
    const ctx = resolveSectionContext(m);
    if (ctx.section && m.contentIndex != null) removeContentAt(ctx.section, m.contentIndex);
  }
  active.value = [];
};

const canMoveUp = computed(() => {
  const m = selectedMeta.value;
  if (!m) return false;
  if (m.kind === 'Section') {
    const ctx = resolveSectionContext(m);
    return !!ctx.list && ctx.index > 0;
  } else if (m.kind === 'ContentItem') {
    const ctx = resolveSectionContext(m);
    return !!ctx.section && (m.contentIndex as any) > 0;
  }
  return false;
});

const canMoveDown = computed(() => {
  const m = selectedMeta.value;
  if (!m) return false;
  if (m.kind === 'Section') {
    const ctx = resolveSectionContext(m);
    return !!ctx.list && ctx.index < ctx.list.length - 1;
  } else if (m.kind === 'ContentItem') {
    const ctx = resolveSectionContext(m);
    const len = (ctx.section?.content || []).length;
    return !!ctx.section && (m.contentIndex as any) < len - 1;
  }
  return false;
});

// Selected section and its content type for homogeneity enforcement
const selectedSection = computed<ContentSection | null>(() => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'Section') return null;
  const ctx = resolveSectionContext(m);
  return ctx.section;
});
const selectedSectionContentType = computed<'None' | 'Link' | 'Text' | 'CollectionsByIds'>(() => {
  const sec = selectedSection.value;
  return sec ? sectionContentType(sec) : 'None';
});
const disableAddLink = computed(() => {
  const t = selectedSectionContentType.value;
  return !(t === 'None' || t === 'Link');
});
const disableAddCollections = computed(() => {
  const t = selectedSectionContentType.value;
  return !(t === 'None' || t === 'CollectionsByIds');
});
const disableAddText = computed(() => {
  const t = selectedSectionContentType.value;
  return !(t === 'None' || t === 'Text');
});

const moveSelectedUp = () => {
  const m = selectedMeta.value;
  if (!m) return;
  if (m.kind === 'Section') {
    const ctx = resolveSectionContext(m);
    if (!ctx.list) return;
    const i = ctx.index;
    if (i <= 0) return;
    // Swap items
    const tmp = ctx.list[i - 1];
    ctx.list[i - 1] = ctx.list[i];
    ctx.list[i] = tmp as any;
    pushUpdate();
    // Reselect the moved section with its new index
    const newIndex = i - 1;
    const oldId = m.id;
    const newId = /^s-\d+$/.test(oldId)
      ? `s-${newIndex}`
      : oldId.replace(/-s-(\d+)$/, `-s-${newIndex}`);
    ensureOpen(newId);
    lastClickedId.value = newId;
    active.value = [newId];
  } else if (m.kind === 'ContentItem') {
    const ctx = resolveSectionContext(m);
    const sec = ctx.section;
    if (!sec) return;
    const from = m.contentIndex as number;
    const to = from - 1;
    if (to < 0) return;
    moveContent(sec, from, to);
    // Update selection to new content index
    const oldId = m.id;
    const newId = oldId.replace(/-c-(\d+)$/, `-c-${to}`);
    ensureOpen(newId);
    lastClickedId.value = newId;
    active.value = [newId];
  }
};

const moveSelectedDown = () => {
  const m = selectedMeta.value;
  if (!m) return;
  if (m.kind === 'Section') {
    const ctx = resolveSectionContext(m);
    if (!ctx.list) return;
    const i = ctx.index;
    if (i >= ctx.list.length - 1) return;
    const tmp = ctx.list[i + 1];
    ctx.list[i + 1] = ctx.list[i];
    ctx.list[i] = tmp as any;
    pushUpdate();
    const newIndex = i + 1;
    const oldId = m.id;
    const newId = /^s-\d+$/.test(oldId)
      ? `s-${newIndex}`
      : oldId.replace(/-s-(\d+)$/, `-s-${newIndex}`);
    ensureOpen(newId);
    lastClickedId.value = newId;
    active.value = [newId];
  } else if (m.kind === 'ContentItem') {
    const ctx = resolveSectionContext(m);
    const sec = ctx.section;
    if (!sec) return;
    const from = m.contentIndex as number;
    const to = from + 1;
    const list = sec.content || [];
    if (to >= list.length) return;
    moveContent(sec, from, to);
    const oldId = m.id;
    const newId = oldId.replace(/-c-(\d+)$/, `-c-${to}`);
    ensureOpen(newId);
    lastClickedId.value = newId;
    active.value = [newId];
  }
};

// Add content buttons for selected section
const addLinkToSelectedSection = () => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'Section') return;
  const ctx = resolveSectionContext(m);
  if (!ctx.section) return;
  if (!canAddTypeToSection(ctx.section, 'Link')) return;
  const newIdx = addContentToSection(ctx.section, 'Link');
  if (newIdx >= 0) {
    ensureOpen(m.id);
    active.value = [`${m.id}-c-${newIdx}`];
    lastClickedId.value = active.value[0];
  }
};
const addCollectionsToSelectedSection = () => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'Section') return;
  const ctx = resolveSectionContext(m);
  if (!ctx.section) return;
  if (!canAddTypeToSection(ctx.section, 'CollectionsByIds')) return;
  const newIdx = addContentToSection(ctx.section, 'CollectionsByIds');
  if (newIdx >= 0) {
    ensureOpen(m.id);
    active.value = [`${m.id}-c-${newIdx}`];
    lastClickedId.value = active.value[0];
  }
};
const addTextToSelectedSection = () => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'Section') return;
  const ctx = resolveSectionContext(m);
  if (!ctx.section) return;
  if (!canAddTypeToSection(ctx.section, 'Text')) return;
  const newIdx = addContentToSection(ctx.section, 'Text');
  if (newIdx >= 0) {
    ensureOpen(m.id);
    active.value = [`${m.id}-c-${newIdx}`];
    lastClickedId.value = active.value[0];
  }
};

// Derived values for right panel bindings
const selectedContentItem = computed<any>(() => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem') return null;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return null;
  return (sec.content || [])[m.contentIndex];
});

const selectedDisplayType = computed<string>(() => {
  const it = selectedContentItem.value;
  if (!it || it.type !== 'Link') return 'None';
  return it.displayConfiguration ? it.displayConfiguration.type : 'None';
});

// Proxy for v-model on v-btn-toggle (Vue 2)
const selectedDisplayTypeProxy = computed<any>({
  get: () => selectedDisplayType.value,
  set: (v: any) => setSelectedLinkDisplayType(v),
});

const selectedBox = computed<any>(() => {
  const it = selectedContentItem.value;
  return it && it.type === 'Link' && it.displayConfiguration && it.displayConfiguration.type === 'Box'
    ? it.displayConfiguration
    : null;
});

const selectedImage = computed<any>(() => {
  const it = selectedContentItem.value;
  return it && it.type === 'Link' && it.displayConfiguration && it.displayConfiguration.type === 'Image'
    ? it.displayConfiguration
    : null;
});

const selectedLinkName = computed<string>(() => {
  const it = selectedContentItem.value;
  return it && it.type === 'Link' ? it.name || '' : '';
});

const selectedChildName = computed<string>(() => {
  const it = selectedContentItem.value;
  return it && it.type === 'Link' && it.target ? it.target.name || '' : '';
});

const selectedChildSections = computed<ContentSection[]>(() => {
  const it = selectedContentItem.value;
  return it && it.type === 'Link' && it.target ? it.target.sections || [] : [];
});

const selectedTextName = computed<string>(() => {
  const it = selectedContentItem.value;
  return it && it.type === 'Text' ? it.name || '' : '';
});

const contentCollectionIds = ref<string[]>([]);

watch([selectedContentItem], () => {
  const it = selectedContentItem.value;
  if (it && it.type === 'CollectionsByIds') {
    contentCollectionIds.value = it.ids;
    pushUpdate();
  }
});

const selectedTextBodyRef = ref('');
watch([selectedTextBodyRef], () => {
  setSelectedTextBody(selectedTextBodyRef.value);
});
watchEffect(() => {
  const it = selectedContentItem.value;
  selectedTextBodyRef.value = it && it.type === 'Text' ? it.text || '' : '';
});

const selectedIsSection = computed(() => (selectedMeta.value ? selectedMeta.value.kind === 'Section' : false));
const selectedIsContent = computed(() => (selectedMeta.value ? selectedMeta.value.kind === 'ContentItem' : false));
const selectedIsChildSections = computed(() =>
  selectedMeta.value ? selectedMeta.value.kind === 'ChildSections' : false,
);
const selectedIsLink = computed(
  () => selectedIsContent.value && selectedContentItem.value && selectedContentItem.value.type === 'Link',
);
const selectedIsText = computed(
  () => selectedIsContent.value && selectedContentItem.value && selectedContentItem.value.type === 'Text',
);
const selectedIsCollections = computed(
  () => selectedIsContent.value && selectedContentItem.value && selectedContentItem.value.type === 'CollectionsByIds',
);

const dummy = async (file: File, type: 'Poster' | 'Cover' | 'Inline') => {
  return 'foo';
};

// ---------------------- Copy/Paste functionality ----------------------
const clipboard = ref<{
  data: any;
  type: 'Section' | 'Link' | 'Text' | 'CollectionsByIds';
  displayType?: 'Box' | 'Image';
} | null>(null);

const copySelected = () => {
  const m = selectedMeta.value;
  if (!m) return;

  if (m.kind === 'Section') {
    const ctx = resolveSectionContext(m);
    if (ctx.section) {
      // Deep clone the section
      clipboard.value = {
        data: JSON.parse(JSON.stringify(ctx.section)),
        type: 'Section',
      };
      eventBus.$emit('snackbarV2.open', { text: 'Section copied', color: 'success' });
    }
  } else if (m.kind === 'ContentItem') {
    const ctx = resolveSectionContext(m);
    if (ctx.section && m.contentIndex != null) {
      const item = ctx.section.content[m.contentIndex];
      // Deep clone the content item
      const clonedItem = JSON.parse(JSON.stringify(item));
      const contentType = (item as any).type;
      let displayType: 'Box' | 'Image' | undefined;

      if (contentType === 'Link' && (item as any).displayConfiguration) {
        displayType = (item as any).displayConfiguration.type;
      }

      clipboard.value = {
        data: clonedItem,
        type: contentType,
        displayType,
      };
      eventBus.$emit('snackbarV2.open', { text: `${contentType} copied`, color: 'success' });
    }
  }
};

const canPaste = computed(() => {
  if (!clipboard.value || !selectedMeta.value) return false;
  const m = selectedMeta.value;

  // Pasting sections
  if (clipboard.value.type === 'Section') {
    // Can paste to another section position (will insert after) or to ChildSections node
    return m.kind === 'Section' || m.kind === 'ChildSections';
  }

  // Pasting content items (Link, Text, CollectionsByIds)
  if (m.kind === 'Section') {
    const ctx = resolveSectionContext(m);
    if (!ctx.section) return false;

    // Check if section allows this content type
    if (!canAddTypeToSection(ctx.section, clipboard.value.type as any)) return false;

    // Additional check for Links: ensure display type matches if section already has Links
    if (clipboard.value.type === 'Link' && clipboard.value.displayType) {
      const existingContent = ctx.section.content || [];
      const existingLinks = existingContent.filter((c: any) => c.type === 'Link');

      if (existingLinks.length > 0) {
        // Check if all existing links have the same display type
        const firstLinkDisplay = (existingLinks[0] as any).displayConfiguration?.type;
        if (firstLinkDisplay && firstLinkDisplay !== clipboard.value.displayType) {
          return false; // Display type mismatch
        }
      }
    }

    return true;
  }

  return false;
});

const pasteToSelected = () => {
  if (!clipboard.value || !selectedMeta.value || !canPaste.value) return;
  const m = selectedMeta.value;

  if (clipboard.value.type === 'Section') {
    // Clone the section data and give it a new ID
    const newSection: ContentSection = JSON.parse(JSON.stringify(clipboard.value.data));
    newSection.id = nanoid(5);

    if (m.kind === 'Section') {
      // Insert after the selected section
      const ctx = resolveSectionContext(m);
      if (ctx.list) {
        const insertIndex = ctx.index + 1;
        ctx.list.splice(insertIndex, 0, newSection);
        pushUpdate();

        // Select the newly pasted section
        const newId = /^s-\d+$/.test(m.id)
          ? `s-${insertIndex}`
          : m.id.replace(/-s-(\d+)$/, `-s-${insertIndex}`);
        ensureOpen(newId);
        lastClickedId.value = newId;
        active.value = [newId];

        eventBus.$emit('snackbarV2.open', { text: 'Section pasted', color: 'success' });
      }
    } else if (m.kind === 'ChildSections') {
      // Add to child sections
      const ctx = resolveSectionContext(m);
      if (ctx.link) {
        if (!ctx.link.target) ctx.link.target = { name: 'View', sections: [] } as any;
        const sections = ctx.link.target.sections || [];
        sections.push(newSection);
        ctx.link.target.sections = sections;
        pushUpdate();

        // Select the newly pasted section
        const newIdx = sections.length - 1;
        const newId = `${m.id}-s-${newIdx}`;
        ensureOpen(newId);
        lastClickedId.value = newId;
        active.value = [newId];

        eventBus.$emit('snackbarV2.open', { text: 'Section pasted', color: 'success' });
      }
    }
  } else {
    // Pasting content item
    if (m.kind === 'Section') {
      const ctx = resolveSectionContext(m);
      if (ctx.section) {
        const newItem = JSON.parse(JSON.stringify(clipboard.value.data));
        const content = ctx.section.content || [];
        content.push(newItem);
        ctx.section.content = content;
        pushUpdate();

        // Select the newly pasted item
        const newIdx = content.length - 1;
        const newId = `${m.id}-c-${newIdx}`;
        ensureOpen(newId);
        lastClickedId.value = newId;
        active.value = [newId];

        eventBus.$emit('snackbarV2.open', { text: `${clipboard.value.type} pasted`, color: 'success' });
      }
    }
  }
};

// Keyboard shortcuts - only for treeview
const handleTreeviewKeyDown = (e: KeyboardEvent) => {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const ctrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

  if (ctrlOrCmd && e.key === 'c' && selectedMeta.value) {
    e.preventDefault();
    copySelected();
  } else if (ctrlOrCmd && e.key === 'v' && canPaste.value) {
    e.preventDefault();
    pasteToSelected();
  }
};

onBeforeMount(async () => {
  await fetchItems();
  window.addEventListener('keydown', handleTreeviewKeyDown);
});

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleTreeviewKeyDown);
});

// When ChildSections node is selected, offer section management for that link
const addSectionUnderSelectedLink = () => {
  const m = selectedMeta.value;
  if (!m || !selectedIsChildSections.value) return;
  const ctx = resolveSectionContext(m);
  const link = ctx.link;
  if (!link) return;
  if (!link.target) link.target = { name: 'View', sections: [] } as any;
  const current = link.target.sections || [];
  const next: ContentSection[] = [...current, { type: 'Content', id: nanoid(5), content: [] }];
  link.target.sections = next;
  pushUpdate();
  // Expand the child sections node and select the newly added section
  const newIdx = next.length - 1;
  ensureOpen(m.id);
  active.value = [`${m.id}-s-${newIdx}`];
  lastClickedId.value = active.value[0];
};

const swatches = [
  ['#B4F1FD'], ['#FFE2B2'], ['#BCF5BC'], ['#E3C2FF'], ['#C0CBF2']
]


const updateCollectionIds = (id: string) => {
  const m = selectedMeta.value;
  if (!m || m.kind !== 'ContentItem' || m.subtype !== 'CollectionsByIds') return;
  const ctx = resolveSectionContext(m);
  const sec = ctx.section;
  if (!sec || m.contentIndex == null) return;
  const item: any = (sec.content || [])[m.contentIndex];
  if (item && item.type === 'CollectionsByIds') {
    contentCollectionIds.value = contentCollectionIds.value.filter(i => i !== id);
    item.ids = contentCollectionIds.value;
  }
}

return { __sfc: true,props, emit, sections, pushUpdate, isLoading, selectedColl, items, fetchItems, addCollections, open, active, lastClickedId, selectNode, onActiveUpdate, ensureOpen, metaMap, deriveTitle, getSectionByPath, getSectionsArrayAtPath, updateCounter, treeItems, getMetaIcon, selectedId, selectedMeta, isActiveSection, resolveSectionContext, sectionContentType, canAddTypeToSection, addRootSection, removeSectionAt, addContentToSection, moveContent, removeContentAt, setSelectedLinkName, setSelectedLinkDisplayType, setSelectedBoxField, setSelectedImageField, setSelectedChildName, setSelectedChildSections, setSelectedTextName, setSelectedTextBody, canRemoveSelected, removeSelected, canMoveUp, canMoveDown, selectedSection, selectedSectionContentType, disableAddLink, disableAddCollections, disableAddText, moveSelectedUp, moveSelectedDown, addLinkToSelectedSection, addCollectionsToSelectedSection, addTextToSelectedSection, selectedContentItem, selectedDisplayType, selectedDisplayTypeProxy, selectedBox, selectedImage, selectedLinkName, selectedChildName, selectedChildSections, selectedTextName, contentCollectionIds, selectedTextBodyRef, selectedIsSection, selectedIsContent, selectedIsChildSections, selectedIsLink, selectedIsText, selectedIsCollections, dummy, clipboard, copySelected, canPaste, pasteToSelected, handleTreeviewKeyDown, addSectionUnderSelectedLink, swatches, updateCollectionIds, TextEditor }
}

})